import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Floating back + cart controls over the product media gallery (mobile).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} backButton
 * @property {HTMLButtonElement} cartButton
 * @property {HTMLElement} [cartCount]
 *
 * @extends {Component<Refs>}
 */
class ProductMediaFloatingActions extends Component {
  requiredRefs = ['backButton', 'cartButton'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartUpdate);
  }

  /**
   * Close / go back to the previous page when possible.
   */
  goBack = () => {
    const fallback = this.dataset.fallbackUrl || '/';

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = fallback;
  };

  /**
   * Open the theme cart drawer (visible instance).
   */
  openCart = () => {
    const drawers = document.querySelectorAll('cart-drawer-component');

    for (const drawer of drawers) {
      if (!(drawer instanceof HTMLElement)) continue;
      if (this.#isHidden(drawer)) continue;

      if (typeof /** @type {{ open?: () => void }} */ (drawer).open === 'function') {
        /** @type {{ open: () => void }} */ (drawer).open();
        return;
      }

      const trigger =
        drawer.querySelector('[data-testid="cart-drawer-trigger"]') ||
        drawer.querySelector('button[aria-haspopup="dialog"]');
      if (trigger instanceof HTMLElement) {
        trigger.click();
        return;
      }
    }

    const cartUrl = typeof Theme !== 'undefined' && Theme.routes?.cart_url ? Theme.routes.cart_url : '/cart';
    window.location.href = cartUrl;
  };

  /**
   * @param {Event} [_event]
   */
  #onCartUpdate = (_event) => {
    void this.#refreshCount();
  };

  async #refreshCount() {
    const countEl = this.refs.cartCount;
    if (!(countEl instanceof HTMLElement)) return;

    const numberCandidate = countEl.querySelector('.cart-text-count__number');
    const numberEl = numberCandidate instanceof HTMLElement ? numberCandidate : countEl;

    try {
      const cartUrl = typeof Theme !== 'undefined' && Theme.routes?.cart_url ? Theme.routes.cart_url : '/cart';
      const endpoint = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      const count = Number(cart?.item_count) || 0;

      if (count <= 0 || count >= 100) {
        numberEl.textContent = '';
        numberEl.classList.add('hidden');
        countEl.classList.add('visually-hidden');
        return;
      }

      const padded = count < 10 ? `0${count}` : String(count);
      numberEl.textContent = `[${padded}]`;
      numberEl.classList.remove('hidden');
      countEl.classList.remove('visually-hidden');
    } catch {
      // Keep last painted count.
    }
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isHidden(element) {
    if (typeof element.checkVisibility === 'function') {
      return !element.checkVisibility();
    }

    return element.offsetParent === null && element.getClientRects().length === 0;
  }
}

if (!customElements.get('product-media-floating-actions')) {
  customElements.define('product-media-floating-actions', ProductMediaFloatingActions);
}
